import { brainerClient } from './client'
import type {
  BIngredient, BTrait, BCup, BCupTrait, BCupRelation,
  BPreparation, BPreparationStep, BStep, BStraw, BStrawHeader,
  BStrawQuery, BStrawBody, BStrawResponse, BStrawResponseObject,
  BStepType, BDrinker, BInterviewer, BInterviewerPart, BInterviewerPartItem,
  BInterviewerPartItemType, BStepFunnelConnector, BConnector, BConnectorCondition,
  BCoffee, BBean, BFlavor, BJourney, BJourneyStep, BJourneyStepLog,
  BJourneyInterviewerForm,
  BrainerAuditLog, BStepGenerator, BStepGeneratorOutput, BStepDocumentGenerator,
  BPlate, BPlateDetail, BPlateSection, BPlateView, BPlateInputParam, BPlateRun, BPlateRunResult,
} from '../types'

// ── Collections ───────────────────────────────────────────────────────────────
export const getCollections = () => brainerClient.get<import('../types').BCollection[]>('/collections')
export const createCollection = (d: { name: string; description?: string }) =>
  brainerClient.post<import('../types').BCollection>('/collections', d)
export const updateCollection = (id: string, d: { name?: string; description?: string }) =>
  brainerClient.put<import('../types').BCollection>(`/collections/${id}`, d)
export const deleteCollection = (id: string) => brainerClient.delete(`/collections/${id}`)
export const getCollectionItems = (id: string) =>
  brainerClient.get<import('../types').BCollectionItem[]>(`/collections/${id}/items`)
export const addCollectionItem = (id: string, d: { value: string; position?: number }) =>
  brainerClient.post<import('../types').BCollectionItem>(`/collections/${id}/items`, d)
export const updateCollectionItem = (itemId: string, d: { value: string; position: number }) =>
  brainerClient.put<import('../types').BCollectionItem>(`/collections/items/${itemId}`, d)
export const removeCollectionItem = (itemId: string) =>
  brainerClient.delete(`/collections/items/${itemId}`)

// ── Ingredients ───────────────────────────────────────────────────────────────
export const getIngredients = () => brainerClient.get<BIngredient[]>('/ingredients')
export const createIngredient = (d: { name: string; description?: string }) =>
  brainerClient.post<BIngredient>('/ingredients', d)
export const updateIngredient = (id: string, d: Partial<BIngredient>) =>
  brainerClient.put<BIngredient>(`/ingredients/${id}`, d)
export const deleteIngredient = (id: string) => brainerClient.delete(`/ingredients/${id}`)

// ── Traits ────────────────────────────────────────────────────────────────────
export const getTraits = () => brainerClient.get<BTrait[]>('/traits')
export const createTrait = (d: { title: string; ingredientId: string; collectionId?: string; description?: string; maxValue?: number; minValue?: number; maxLength?: number; minLength?: number }) =>
  brainerClient.post<BTrait>('/traits', d)
export const updateTrait = (id: string, d: Partial<BTrait>) =>
  brainerClient.put<BTrait>(`/traits/${id}`, d)
export const deleteTrait = (id: string) => brainerClient.delete(`/traits/${id}`)

// ── Cups ──────────────────────────────────────────────────────────────────────
export const getCups = () => brainerClient.get<BCup[]>('/cups')
export const getCup  = (id: string) => brainerClient.get<BCup>(`/cups/${id}`)
export const createCup = (d: { title: string; description?: string; disposable?: boolean }) =>
  brainerClient.post<BCup>('/cups', d)
export const updateCup = (id: string, d: Partial<BCup>) =>
  brainerClient.put<BCup>(`/cups/${id}`, d)
export const deleteCup = (id: string) => brainerClient.delete(`/cups/${id}`)
// Cup traits
export const getCupTraits = (cupId: string) => brainerClient.get<BCupTrait[]>(`/cups/${cupId}/traits`)
export const addCupTrait = (cupId: string, d: { traitId: string; position?: number; mandatory?: boolean; uniqueField?: boolean; isIdentifier?: boolean }) =>
  brainerClient.post<BCupTrait>(`/cups/${cupId}/traits`, d)
export const updateCupTrait = (traitId: string, d: Partial<BCupTrait>) =>
  brainerClient.put<BCupTrait>(`/cups/traits/${traitId}`, d)
export const removeCupTrait = (traitId: string) => brainerClient.delete(`/cups/traits/${traitId}`)
// Cup relations
export const getAllCupRelations   = () => brainerClient.get<BCupRelation[]>('/cups/relations')
export const getCupRelations      = (cupId: string) => brainerClient.get<BCupRelation[]>(`/cups/${cupId}/relations`)
export const getCupChildRelations = (cupId: string) => brainerClient.get<BCupRelation[]>(`/cups/${cupId}/child-relations`)
export const addCupRelation = (cupId: string, d: { childCupId: string; label?: string; relationType?: import('../types').BCupRelationType; mandatory?: boolean }) =>
  brainerClient.post<BCupRelation>(`/cups/${cupId}/relations`, d)
export const updateCupRelation = (relationId: string, d: { label?: string | null; relationType?: import('../types').BCupRelationType; mandatory?: boolean }) =>
  brainerClient.put<BCupRelation>(`/cups/relations/${relationId}`, d)
export const removeCupRelation = (relationId: string) => brainerClient.delete(`/cups/relations/${relationId}`)
export const resetAllBrainerData = () => brainerClient.delete('/cups/reset')

// ── Steps ─────────────────────────────────────────────────────────────────────
export const getSteps = () => brainerClient.get<BStep[]>('/steps')
export const createStep = (d: { type: BStepType; title: string; description?: string }) =>
  brainerClient.post<BStep>('/steps', d)
export const updateStep = (id: string, d: Partial<BStep>) => brainerClient.put<BStep>(`/steps/${id}`, d)
export const deleteStep = (id: string) => brainerClient.delete(`/steps/${id}`)
// Straw
export const getStraw = (stepId: string) => brainerClient.get<BStraw>(`/steps/${stepId}/straw`)
export const createStraw = (stepId: string, d: { url: string; method: string }) =>
  brainerClient.post<BStraw>(`/steps/${stepId}/straw`, d)
export const updateStraw = (strawId: string, d: Partial<BStraw>) =>
  brainerClient.put<BStraw>(`/steps/straw/${strawId}`, d)
export const addStrawHeader = (strawId: string, d: { title: string; value: string; description?: string }) =>
  brainerClient.post<BStrawHeader>(`/steps/straw/${strawId}/headers`, d)
export const removeStrawHeader = (headerId: string) => brainerClient.delete(`/steps/straw/headers/${headerId}`)
export const addStrawQuery = (strawId: string, d: { title: string; value: string }) =>
  brainerClient.post<BStrawQuery>(`/steps/straw/${strawId}/queries`, d)
export const removeStrawQuery = (queryId: string) => brainerClient.delete(`/steps/straw/queries/${queryId}`)
export const addStrawBody = (strawId: string, d: { key: string; value: string }) =>
  brainerClient.post<BStrawBody>(`/steps/straw/${strawId}/bodies`, d)
export const updateStrawBody = (bodyId: string, d: Partial<BStrawBody>) =>
  brainerClient.put<BStrawBody>(`/steps/straw/bodies/${bodyId}`, d)
export const removeStrawBody = (bodyId: string) => brainerClient.delete(`/steps/straw/bodies/${bodyId}`)
export const addStrawResponse = (strawId: string, d: { code: number }) =>
  brainerClient.post<BStrawResponse>(`/steps/straw/${strawId}/responses`, d)
export const removeStrawResponse = (responseId: string) => brainerClient.delete(`/steps/straw/responses/${responseId}`)
export const addStrawResponseObject = (responseId: string, d: { title: string; value?: string }) =>
  brainerClient.post<BStrawResponseObject>(`/steps/straw/responses/${responseId}/objects`, d)
export const removeStrawResponseObject = (objectId: string) =>
  brainerClient.delete(`/steps/straw/responses/objects/${objectId}`)
// Drinkers (standalone)
export const getDrinkers = () => brainerClient.get<BDrinker[]>('/drinkers')
export const getDrinker = (id: string) => brainerClient.get<BDrinker>(`/drinkers/${id}`)
export const createDrinker = (d: { title: string; script: string; resultKey: string }) =>
  brainerClient.post<BDrinker>('/drinkers', d)
export const updateDrinker = (id: string, d: Partial<BDrinker>) =>
  brainerClient.put<BDrinker>(`/drinkers/${id}`, d)
export const deleteDrinker = (id: string) => brainerClient.delete(`/drinkers/${id}`)
// Interviewer
export const getInterviewer = (stepId: string) => brainerClient.get<BInterviewer>(`/steps/${stepId}/interviewer`)
export const createInterviewer = (stepId: string, d: { cupId: string; payloadKey?: string; postProcessDrinkerId?: string; storageDatasetId?: string; layout?: 'wizard' | 'page' }) =>
  brainerClient.post<BInterviewer>(`/steps/${stepId}/interviewer`, d)
export const updateInterviewer = (interviewerId: string, d: { cupId?: string; payloadKey?: string; postProcessDrinkerId?: string | null; storageDatasetId?: string | null; layout?: 'wizard' | 'page' }) =>
  brainerClient.put<BInterviewer>(`/steps/interviewer/${interviewerId}`, d)
// Interviewer parts
export const getInterviewerParts = (interviewerId: string) =>
  brainerClient.get<BInterviewerPart[]>(`/steps/interviewer/${interviewerId}/parts`)
export const addInterviewerPart = (interviewerId: string, d: { title: string; position?: number }) =>
  brainerClient.post<BInterviewerPart>(`/steps/interviewer/${interviewerId}/parts`, d)
export const updateInterviewerPart = (partId: string, d: { title?: string; position?: number }) =>
  brainerClient.put<BInterviewerPart>(`/steps/interviewer/parts/${partId}`, d)
export const removeInterviewerPart = (partId: string) =>
  brainerClient.delete(`/steps/interviewer/parts/${partId}`)
// Interviewer part items
export const addInterviewerPartItem = (partId: string, d: {
  type: BInterviewerPartItemType; cupTraitId?: string; cupRelationId?: string; position?: number
  inputMode?: import('../types').BInterviewerPartItemInputMode; payloadKey?: string
  createNewPlateId?: string; label?: string; searchFields?: string[]; viaCupRelationIds?: string[]
  globalRelationId?: string
}) =>
  brainerClient.post<BInterviewerPartItem>(`/steps/interviewer/parts/${partId}/items`, d)
export const updateInterviewerPartItem = (itemId: string, d: {
  label?: string | null; altPreparationId?: string | null; altLabel?: string | null
  createNewPlateId?: string | null; position?: number
  inputMode?: import('../types').BInterviewerPartItemInputMode
}) =>
  brainerClient.put<BInterviewerPartItem>(`/steps/interviewer/parts/items/${itemId}`, d)
export const removeInterviewerPartItem = (itemId: string) =>
  brainerClient.delete(`/steps/interviewer/parts/items/${itemId}`)
// FunnelConnector step
export const getStepFunnelConnector = (stepId: string) => brainerClient.get<BStepFunnelConnector | null>(`/steps/${stepId}/funnel-connector`)
export const setStepFunnelConnector = (stepId: string, d: { funnelConnectorId: string }) =>
  brainerClient.post<BStepFunnelConnector>(`/steps/${stepId}/funnel-connector`, d)

// ── Preparations ──────────────────────────────────────────────────────────────
export const getPreparations = () => brainerClient.get<BPreparation[]>('/preparations')
export const createPreparation = (d: { name: string; description?: string; scheduled?: boolean; clock?: string; eventSubscription?: string; forwardingPlateId?: string }) =>
  brainerClient.post<BPreparation>('/preparations', d)
export const updatePreparation = (id: string, d: Partial<BPreparation>) =>
  brainerClient.put<BPreparation>(`/preparations/${id}`, d)
export const deletePreparation = (id: string) => brainerClient.delete(`/preparations/${id}`)
export const getPreparationSteps = (id: string) =>
  brainerClient.get<BPreparationStep[]>(`/preparations/${id}/steps`)
export const addPreparationStep = (id: string, d: { stepId: string; mandatory?: boolean; position?: number }) =>
  brainerClient.post<BPreparationStep>(`/preparations/${id}/steps`, d)
export const removePreparationStep = (stepId: string) =>
  brainerClient.delete(`/preparations/steps/${stepId}`)

// ── Connectors ────────────────────────────────────────────────────────────────
export const getConnectors = (preparationId: string) =>
  brainerClient.get<BConnector[]>('/connectors', { params: { preparationId } })
export const createConnector = (d: { preparationId: string; fromStepId: string; toStepId: string }) =>
  brainerClient.post<BConnector>('/connectors', d)
export const deleteConnector = (id: string) => brainerClient.delete(`/connectors/${id}`)
export const getConnectorConditions = (id: string) =>
  brainerClient.get<BConnectorCondition[]>(`/connectors/${id}/conditions`)
export const addConnectorCondition = (id: string, d: { script: string }) =>
  brainerClient.post<BConnectorCondition>(`/connectors/${id}/conditions`, d)
export const removeConnectorCondition = (conditionId: string) =>
  brainerClient.delete(`/connectors/conditions/${conditionId}`)

// ── Coffees ───────────────────────────────────────────────────────────────────
export const getCoffees = (cupId?: string) =>
  brainerClient.get<BCoffee[]>('/coffees', { params: cupId ? { cupId } : undefined })
export const getCoffeesPaginated = (cupId: string, page: number, limit: number) =>
  brainerClient.get<{ rows: BCoffee[]; total: number }>('/coffees/paged', { params: { cupId, page, limit } })
export const getCoffee = (id: string) => brainerClient.get<BCoffee>(`/coffees/${id}`)
export const createCoffee = (d: { cupId: string }) => brainerClient.post<BCoffee>('/coffees', d)
export const deleteCoffee = (id: string) => brainerClient.delete(`/coffees/${id}`)
export const getBeans = (coffeeId: string) => brainerClient.get<BBean[]>(`/coffees/${coffeeId}/beans`)
export const addBean = (coffeeId: string, d: { cupTraitId: string; chunk?: string }) =>
  brainerClient.post<BBean>(`/coffees/${coffeeId}/beans`, d)
export const updateBean = (beanId: string, d: { chunk?: string }) =>
  brainerClient.put<BBean>(`/coffees/beans/${beanId}`, d)
export const removeBean = (beanId: string) => brainerClient.delete(`/coffees/beans/${beanId}`)
export const getFlavors = (coffeeId: string) => brainerClient.get<BFlavor[]>(`/coffees/${coffeeId}/flavors`)
export const addFlavor = (coffeeId: string, d: { cupRelationId: string; childCoffeeId: string }) =>
  brainerClient.post<BFlavor>(`/coffees/${coffeeId}/flavors`, d)
export const removeFlavor = (flavorId: string) => brainerClient.delete(`/coffees/flavors/${flavorId}`)
// A whole record from a form: trait values keyed by cupTraitId, parent links keyed by
// cupRelationId. 422 with per-field errors[] when the cup's rules aren't met.
export const createRecord = (d: {
  cupId: string
  traits: Record<string, string>
  relations: Record<string, { type: import('../types').BCupRelationType; parentCoffeeId?: string; parentCoffeeIds?: string[] }>
  globals?: Record<string, import('../types').BGlobalTarget[]>
}) => brainerClient.post<{ id: string }>('/coffees/records', d)

// ── Global relations (cup → mailbox / user / dataset) ──
export const getGlobalClasses = () => brainerClient.get<import('../types').BGlobalClassInfo[]>('/cups/global-classes')
export const getCupGlobalRelations = (cupId: string) =>
  brainerClient.get<import('../types').BCupGlobalRelation[]>(`/cups/${cupId}/global-relations`)
export const addCupGlobalRelation = (cupId: string, d: { label: string; targetClass: import('../types').BGlobalClass; relationType: import('../types').BGlobalRelationType; mandatory?: boolean }) =>
  brainerClient.post<import('../types').BCupGlobalRelation>(`/cups/${cupId}/global-relations`, d)
export const updateCupGlobalRelation = (relationId: string, d: { label?: string; relationType?: import('../types').BGlobalRelationType; mandatory?: boolean }) =>
  brainerClient.put<import('../types').BCupGlobalRelation>(`/cups/global-relations/${relationId}`, d)
export const removeCupGlobalRelation = (relationId: string) => brainerClient.delete(`/cups/global-relations/${relationId}`)
export const getCoffeeGlobalLinks = (coffeeId: string) =>
  brainerClient.get<import('../types').BCoffeeGlobalLinks[]>(`/coffees/${coffeeId}/global-links`)
export const setCoffeeGlobalLinks = (coffeeId: string, relationId: string, targets: import('../types').BGlobalTarget[]) =>
  brainerClient.put<import('../types').BCoffeeGlobalLinks[]>(`/coffees/${coffeeId}/global-links/${relationId}`, { targets })
export const checkValueUnique = (cupTraitId: string, value: string, excludeCoffeeId?: string) =>
  brainerClient.get<{ unique: boolean }>('/coffees/unique-check', {
    params: { cupTraitId, value, ...(excludeCoffeeId ? { excludeCoffeeId } : {}) },
  })
export const searchCoffeesByIdentifier =(cupId: string, q: string, traitTitles?: string[]) =>
  brainerClient.get<{ id: string; label: string }[]>('/coffees/search', {
    params: { cupId, q, ...(traitTitles?.length ? { traitTitles: traitTitles.join(',') } : {}) },
  })

export const getCoffeesResolved = (cupId: string, page: number, limit: number) =>
  brainerClient.get<Record<string, any>>('/coffees/resolved', { params: { cupId, page, limit } })
export const getCoffeeResolved = (id: string) =>
  brainerClient.get<Record<string, any>>(`/coffees/${id}/resolved`)
export const getChildCoffees = (parentCoffeeId: string, childCupTitle: string) =>
  brainerClient.get<Record<string, any>>(`/coffees/by-parent/${parentCoffeeId}`, { params: { childCupTitle } })

// ── Journeys ──────────────────────────────────────────────────────────────────
export const getJourneys = () => brainerClient.get<BJourney[]>('/journeys')
export const getJourney  = (id: string) => brainerClient.get<BJourney>(`/journeys/${id}`)
export const getJourneysByPreparation = (preparationId: string, page: number, limit = 20) =>
  brainerClient.get<{
    data: BJourney[]; total: number; page: number; totalPages: number;
    completedCount: number; failedCount: number; runningCount: number;
  }>(`/journeys/by-preparation/${preparationId}?page=${page}&limit=${limit}`)
export const getJourneySteps = (id: string) => brainerClient.get<BJourneyStep[]>(`/journeys/${id}/steps`)
export const getJourneyStepLogs = (stepId: string) => brainerClient.get<BJourneyStepLog[]>(`/journeys/steps/${stepId}/logs`)
export const triggerJourney = (preparationId: string, initialPayload?: Record<string, string>) =>
  brainerClient.post<BJourney>('/journeys/trigger', { preparationId, initialPayload })
export const getJourneyInterviewer = (id: string) =>
  brainerClient.get<BJourneyInterviewerForm>(`/journeys/${id}/interviewer`)
export const submitJourneyInterviewer = (id: string, d: {
  traits: Record<string, string>
  relations: Record<string, {
    type: import('../types').BCupRelationType
    parentCoffeeId?: string
    parentCoffeeIds?: string[]
    beans?: Record<string, string>
  }>
  // Keyed by InterviewerPartItem.id — only present for items with viaCupRelationIds (chained
  // trait/relation targets reached through a relation path, e.g. a document upload two hops away).
  chainedAnswers?: Record<string, string | { documentId: string; filename: string } | {
    type: import('../types').BCupRelationType
    parentCoffeeId?: string
    parentCoffeeIds?: string[]
    beans?: Record<string, string>
  }>
  globals?: Record<string, import('../types').BGlobalTarget[]>
}) => brainerClient.post<{ journey: BJourney; output: Record<string, unknown>; forwardingPlateId?: string | null }>(`/journeys/${id}/interviewer`, d)
export const stopJourney = (id: string) =>
  brainerClient.post<BJourney>(`/journeys/${id}/stop`)

// ── Step Generators ───────────────────────────────────────────────────────────
export const getStepGenerator = (stepId: string) =>
  brainerClient.get<BStepGenerator | null>(`/step-generators/by-step/${stepId}`)
export const createStepGenerator = (stepId: string, coffeePayloadKey = 'coffeeId') =>
  brainerClient.post<BStepGenerator>('/step-generators', { stepId, coffeePayloadKey })
export const updateStepGenerator = (id: string, data: { coffeePayloadKey?: string }) =>
  brainerClient.put<BStepGenerator>(`/step-generators/${id}`, data)
export const deleteStepGenerator = (id: string) =>
  brainerClient.delete(`/step-generators/${id}`)
export const addGeneratorOutput = (generatorId: string, data: {
  targetTraitTitle: string; template: string; overwriteIfSet?: boolean; position?: number
}) => brainerClient.post<BStepGeneratorOutput>(`/step-generators/${generatorId}/outputs`, data)
export const updateGeneratorOutput = (outputId: string, data: {
  targetTraitTitle?: string; template?: string; overwriteIfSet?: boolean; position?: number
}) => brainerClient.put<BStepGeneratorOutput>(`/step-generators/outputs/${outputId}`, data)

// ── Step Document Generators ──────────────────────────────────────────────────
export const getStepDocumentGenerator = (stepId: string) =>
  brainerClient.get<BStepDocumentGenerator | null>(`/step-document-generators/by-step/${stepId}`)
export const createStepDocumentGenerator = (data: {
  stepId: string; templateId: string; datasetId: string; coffeePayloadKey?: string; script?: string; cupRelationId: string
}) => brainerClient.post<BStepDocumentGenerator>('/step-document-generators', data)
export const updateStepDocumentGenerator = (id: string, data: {
  templateId?: string; datasetId?: string; coffeePayloadKey?: string; script?: string; cupRelationId?: string
}) => brainerClient.put<BStepDocumentGenerator>(`/step-document-generators/${id}`, data)
export const deleteStepDocumentGenerator = (id: string) =>
  brainerClient.delete(`/step-document-generators/${id}`)
export const deleteGeneratorOutput = (outputId: string) =>
  brainerClient.delete(`/step-generators/outputs/${outputId}`)

// ── Audit Logs ────────────────────────────────────────────────────────────────
export const getBrainerAuditLogs = (params: { limit?: number } = {}) =>
  brainerClient.get<{ rows: BrainerAuditLog[]; total: number }>('/audit-logs', { params })

// ── Plates ────────────────────────────────────────────────────────────────────
export const getPlates = () => brainerClient.get<BPlate[]>('/plates')
export const getPlate = (id: string) => brainerClient.get<BPlateDetail>(`/plates/${id}`)
export const createPlate = (d: { name: string; description?: string; subjectType?: string; width?: string; headerTitle?: string }) =>
  brainerClient.post<BPlate>('/plates', d)
export const updatePlate = (id: string, d: { name?: string; description?: string; subjectType?: string; width?: string; headerTitle?: string }) =>
  brainerClient.put<BPlate>(`/plates/${id}`, d)
export const deletePlate = (id: string) => brainerClient.delete(`/plates/${id}`)
export const verifyPlate = (id: string) => brainerClient.post<BPlate>(`/plates/${id}/verify`)
export const deployPlate = (id: string) => brainerClient.post<BPlate>(`/plates/${id}/deploy`)
export const runPlate = (id: string, inputParams: Record<string, string>) =>
  brainerClient.post<BPlateRunResult>(`/plates/${id}/run`, { inputParams })
export const getPlateRuns = (id: string) => brainerClient.get<BPlateRun[]>(`/plates/${id}/runs`)
// Sections
export const addPlateSection = (plateId: string, d: { title?: string; rank: number; width: number }) =>
  brainerClient.post<BPlateSection>(`/plates/${plateId}/sections`, d)
export const updatePlateSection = (sectionId: string, d: { title?: string; rank?: number; width?: number }) =>
  brainerClient.put<BPlateSection>(`/plates/sections/${sectionId}`, d)
export const removePlateSection = (sectionId: string) =>
  brainerClient.delete(`/plates/sections/${sectionId}`)
// Section rows — either a set of views side by side in one <div class="section-row">, or (when
// isTabbed) an independent tab strip whose views become the tabs.
export const addPlateSectionRow = (sectionId: string, rank: number, isTabbed?: boolean) =>
  brainerClient.post<import('../types').BPlateSectionRow>(`/plates/sections/${sectionId}/rows`, { rank, isTabbed })
export const updatePlateSectionRow = (rowId: string, d: { rank?: number; isTabbed?: boolean }) =>
  brainerClient.put<import('../types').BPlateSectionRow>(`/plates/rows/${rowId}`, d)
export const removePlateSectionRow = (rowId: string) =>
  brainerClient.delete(`/plates/rows/${rowId}`)
// Views
export const runDrinker = (id: string, context: Record<string, string>) =>
  brainerClient.post<{ data: Record<string, unknown>; error?: string; logs?: string[] }>(`/drinkers/${id}/run`, { context })
export const addPlateView = (sectionId: string, d: {
  title: string; description?: string; category?: string; plateSectionRowId?: string; maxHeight?: number;
  rank?: number; drinkerId?: string; resultKey?: string; chartType?: string;
  inputParamId?: string; coffeeApiMode?: string; childCupTitle?: string; dataPath?: string;
  columnConfig?: import('../types').BPlateViewColumnDef[];
  rowActions?: import('../types').BPlateViewRowAction[];
  outputMappings?: import('../types').BPlateViewOutputMapping[];
  paginationConfig?: import('../types').BPlateViewPaginationConfig;
  filterConditions?: import('../types').BPlateViewFilterCondition[];
  orderBy?: import('../types').BPlateViewOrderBy[];
  backgroundColor?: string; borderColor?: string; borderStyle?: string;
  borderWidth?: string; borderRadius?: string; preparationId?: string;
  showTitle?: boolean; showDescription?: boolean; spacing?: import('../types').BPlateViewSpacing;
  inline?: boolean; summaryRows?: import('../types').BPlateViewSummaryRow[];
}) => brainerClient.post<BPlateView>(`/plates/sections/${sectionId}/views`, d)
export const updatePlateView = (viewId: string, d: {
  title?: string; description?: string; category?: string; plateSectionRowId?: string; maxHeight?: number;
  rank?: number; drinkerId?: string; resultKey?: string; chartType?: string;
  inputParamId?: string; coffeeApiMode?: string; childCupTitle?: string; dataPath?: string;
  columnConfig?: import('../types').BPlateViewColumnDef[];
  rowActions?: import('../types').BPlateViewRowAction[];
  outputMappings?: import('../types').BPlateViewOutputMapping[];
  paginationConfig?: import('../types').BPlateViewPaginationConfig;
  filterConditions?: import('../types').BPlateViewFilterCondition[];
  orderBy?: import('../types').BPlateViewOrderBy[];
  backgroundColor?: string; borderColor?: string; borderStyle?: string;
  borderWidth?: string; borderRadius?: string; preparationId?: string;
  showTitle?: boolean; showDescription?: boolean; spacing?: import('../types').BPlateViewSpacing;
  inline?: boolean; summaryRows?: import('../types').BPlateViewSummaryRow[];
}) => brainerClient.put<BPlateView>(`/plates/views/${viewId}`, d)
export const removePlateView = (viewId: string) =>
  brainerClient.delete(`/plates/views/${viewId}`)
export const runPlateView = (viewId: string, inputParams: Record<string, string>) =>
  brainerClient.post<{ data: unknown; error?: string }>(`/plates/views/${viewId}/run`, { inputParams })
// Section buttons
export const addPlateSectionButton = (sectionId: string, d: { label: string; destinationPlateId?: string; preparationId?: string; variant?: string; icon?: string; position?: number }) =>
  brainerClient.post<import('../types').BPlateSectionButton>(`/plates/sections/${sectionId}/buttons`, d)
export const updatePlateSectionButton = (buttonId: string, d: { label?: string; destinationPlateId?: string; preparationId?: string; variant?: string; icon?: string; position?: number }) =>
  brainerClient.put<import('../types').BPlateSectionButton>(`/plates/sections/buttons/${buttonId}`, d)
export const removePlateSectionButton = (buttonId: string) =>
  brainerClient.delete(`/plates/sections/buttons/${buttonId}`)
// Global (plate-level) buttons — same shape/lifecycle as section buttons, just scoped to the
// Plate itself (rendered in a workspace navbar instead of a section header row).
export const addPlateButton = (plateId: string, d: { label: string; destinationPlateId?: string; preparationId?: string; variant?: string; icon?: string; position?: number }) =>
  brainerClient.post<import('../types').BPlateSectionButton>(`/plates/${plateId}/buttons`, d)
// Input params
export const addPlateInputParam = (plateId: string, d: { name: string; description?: string; paramType?: string; required?: boolean; fixedValue?: string }) =>
  brainerClient.post<BPlateInputParam>(`/plates/${plateId}/params`, d)
export const updatePlateInputParam = (paramId: string, d: { name?: string; description?: string; paramType?: string; required?: boolean; fixedValue?: string }) =>
  brainerClient.put<BPlateInputParam>(`/plates/params/${paramId}`, d)
export const removePlateInputParam = (paramId: string) =>
  brainerClient.delete(`/plates/params/${paramId}`)
// OpenPlace
export const getOpenPlates = () => brainerClient.get<BPlate[]>('/plates/openplace')
export const publishToOpenPlace = (id: string) => brainerClient.post<BPlate>(`/plates/openplace/${id}/publish`)
export const unpublishFromOpenPlace = (id: string) => brainerClient.delete<BPlate>(`/plates/openplace/${id}/publish`)

// ── Service config ────────────────────────────────────────────────────────────
export const getBrainerConfig = () =>
  brainerClient.get<import('../types').ServiceConfigParam[]>('/config')
export const setBrainerConfig = (key: string, value: string) =>
  brainerClient.post('/config', { [key]: value })

// ── API Keys ──────────────────────────────────────────────────────────────────
export const getBrainerApiKeys = () =>
  brainerClient.get<import('../types').BrainerApiKey[]>('/keys')
export const createBrainerApiKey = (data: { name: string; lifetimeYears: 1 | 2 | 3 }) =>
  brainerClient.post<import('../types').BrainerApiKeyCreated>('/keys', data)
export const revokeBrainerApiKey = (id: string) =>
  brainerClient.delete(`/keys/${id}`)
